import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchMe, login as apiLogin, logout as apiLogout, SIGNED_OUT, type Staff } from "@/services/auth";

type AuthValue = {
  staff: Staff | null;
  // ⛔ A THIRD STATE, not a boolean. On first paint we do not yet know whether the stored token is
  // still good, and treating "unknown" as "logged out" flashes the login screen at someone who is
  // signed in — then yanks it away. The app renders nothing until this is false.
  checking: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((s) => !cancelled && setStaff(s))
      .catch(() => !cancelled && setStaff(null))
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Any request the server refuses as signed out ends the session here too (services/api.ts).
  useEffect(() => {
    const out = () => setStaff(null);
    window.addEventListener(SIGNED_OUT, out);
    return () => window.removeEventListener(SIGNED_OUT, out);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setStaff(await apiLogin(email, password));
  }, []);

  const signOut = useCallback(async () => {
    await apiLogout();
    setStaff(null);
  }, []);

  const value = useMemo(() => ({ staff, checking, signIn, signOut }), [staff, checking, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
